import type { Comp, GameObj, OpacityComp, PosComp, RotateComp, StateComp } from "kaplay";
import { config } from "../config";
import { PX_PER_M } from "../config/constants";
import type { Severity } from "../config/types";
import { emit } from "../core/events";
import { k } from "../core/kaplay";
import type { RunState } from "../core/runState";
import { spin360 } from "../core/tweens";
import { GROUNDED, hasLanded, launchAir, stepAir, type AirState } from "../systems/air";
import { withGripBonus } from "../systems/collision";
import { createMoveState, refillBoost, stepMovement, type MoveInput, type MoveState } from "../systems/movement";
import { pedalRock, pushingSideways, riderPose } from "../systems/riderPose";
import type { LookComp } from "./look";
import type { PlayerInputComp } from "./playerInput";

export type RiderState = "riding" | "air" | "crashed" | "dead";
export const RIDER_STATES: RiderState[] = ["riding", "air", "crashed", "dead"];

export interface RiderComp extends Comp {
    motion: MoveState;
    air: AirState;
    invulnerable: number;
    isAirborne(): boolean;
    wipeout(severity: Severity): void;
    launch(vz: number, dropM?: number): void;
    refillBoost(amount: number): void;
    kill(): void;
}

export type Rider = GameObj<
    RiderComp | PosComp | LookComp | PlayerInputComp | StateComp<RiderState> | OpacityComp | RotateComp
>;

const cfg = config.player;
const air = config.tricks.air;

function emitChanges(before: MoveState, after: MoveState) {
    if (before.skidding !== after.skidding) emit("skid", { on: after.skidding });
    if (!before.boostOn && after.boostOn) emit("boostOn", {});
    if (before.boostOn && !after.boostOn) emit("boostOff", {});
}

function setMove(r: Rider, next: MoveState) {
    emitChanges(r.motion, next);
    r.motion = next;
}

function ride(r: Rider, run: RunState, input: MoveInput) {
    const surface = withGripBonus(config.zones.surfaces[run.surface], run.gripBonus);
    setMove(r, stepMovement(r.motion, input, surface, k.dt(), cfg));
}

function fly(r: Rider, run: RunState) {
    const { intent } = r;
    ride(r, run, { ...intent, steer: intent.steer * air.airTurnFactor, pedal: false, brake: false });
    r.air = stepAir(r.air, k.dt(), air.gravity);
    if (hasLanded(r.air)) r.enterState("riding", r.air);
}

function slide(r: Rider) {
    const speed = Math.max(0, r.motion.speed - cfg.skidBraking * k.dt());
    const rad = (r.motion.heading * Math.PI) / 180;
    r.motion = {
        ...r.motion,
        speed,
        x: r.motion.x + speed * Math.sin(rad) * k.dt(),
        y: r.motion.y + speed * Math.cos(rad) * k.dt(),
    };
}

function startCrash(r: Rider, duration: number) {
    setMove(r, { ...r.motion, boostOn: false, skidding: false, pedalling: false, braking: false });
    r.air = GROUNDED;
    r.pose = "crash";
    r.flipX = r.motion.heading > 0;
    spin360(r, duration * 0.4, r.flipX ? 1 : -1);

    k.wait(duration * 0.4, () => {
        r.angle = 0;
        r.pose = "fallen";
    });
    k.wait(duration, () => {
        if (r.state !== "crashed") return;
        r.motion = { ...r.motion, heading: 0 };
        r.invulnerable = cfg.invulnerableS;
        r.enterState("riding");
        emit("recover", {});
    });
}

function updatePose(r: Rider) {
    if (r.state !== "riding") return;
    const poseInput = { ...r.motion, time: k.time() };
    const { pose, flipX } = riderPose(poseInput);
    r.pose = pose;
    r.flipX = flipX;
    r.angle = pedalRock(poseInput, pushingSideways(r.motion.heading, r.intent.steer, r.intent.targetHeading));
}

export function rider(run: RunState): RiderComp {
    return {
        id: "rider",
        require: ["pos", "look", "playerInput", "state", "opacity", "rotate"],
        motion: createMoveState(cfg),
        air: GROUNDED,
        invulnerable: 0,

        add(this: Rider) {
            this.onStateUpdate("riding", () => ride(this, run, this.intent));
            this.onStateUpdate("air", () => fly(this, run));
            this.onStateUpdate("crashed", () => slide(this));
            this.onStateEnter("crashed", (duration: number) => startCrash(this, duration));
            this.onStateEnter("dead", () => {
                this.motion = { ...this.motion, speed: 0 };
                this.opacity = 0;
            });
        },

        update(this: Rider) {
            this.pos = k.vec2(this.motion.x, this.motion.y).scale(PX_PER_M);
            this.offsetY = -Math.max(0, this.air.height) * PX_PER_M;
            this.invulnerable = Math.max(0, this.invulnerable - k.dt());
            if (this.state !== "dead") this.opacity = this.invulnerable > 0 && k.time() % 0.2 < 0.1 ? 0.3 : 1;
            updatePose(this);

            run.speed = this.motion.speed;
            run.distanceM = this.motion.y;
            run.boost = this.motion.boost;
        },

        isAirborne(this: Rider) {
            return this.state === "air";
        },

        wipeout(this: Rider, severity: Severity) {
            if (this.invulnerable > 0 || this.state === "crashed" || this.state === "dead") return;
            const duration = cfg.crashDurationS[severity];
            if (duration > 0) this.enterState("crashed", duration);
        },

        launch(this: Rider, vz: number, dropM = 0) {
            if (this.state !== "riding") return;
            this.air = launchAir(vz, dropM);
            this.enterState("air");
        },

        refillBoost(this: Rider, amount: number) {
            this.motion = refillBoost(this.motion, amount);
            emit("boostRefill", { amount });
        },

        kill(this: Rider) {
            this.enterState("dead");
        },
    };
}
